import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';

interface HeroProps {
  title: string;
  subtitle: string;
  description?: string;
  primaryCTA?: {
    text: string;
    href?: string;
    onClick?: () => void;
  };
  secondaryCTA?: {
    text: string;
    href?: string;
    onClick?: () => void;
  };
  backgroundImage?: string;
  mobileBackgroundImage?: string;
  height?: 'full' | 'large' | 'medium';
}

const Hero: React.FC<HeroProps> = ({
  title,
  subtitle,
  description,
  primaryCTA,
  secondaryCTA,
  backgroundImage,
  mobileBackgroundImage,
  height = 'full'
}) => {
  // Mobile detection hook
  const [isMobile, setIsMobile] = useState(false);
  
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    // Check on mount
    checkMobile();
    
    // Add resize listener
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Get current image based on device type
  const currentImage = isMobile && mobileBackgroundImage 
    ? mobileBackgroundImage 
    : (backgroundImage || '/images/parallax-cinema-hero-image.png');

  // Preload images for better performance
  useEffect(() => {
    const img = new Image();
    img.src = currentImage;
    
    // Preload the other image for faster switching
    if (mobileBackgroundImage && backgroundImage) {
      const otherImage = isMobile ? backgroundImage : mobileBackgroundImage;
      const otherImg = new Image();
      otherImg.src = otherImage;
    }
  }, [currentImage, backgroundImage, mobileBackgroundImage, isMobile]);

  const heightClasses = {
    full: 'h-screen',
    large: 'h-[80vh]',
    medium: 'h-[60vh]'
  };

  return (
    <section className={`relative ${heightClasses[height]} overflow-hidden`}>
      {/* Dynamic Background */}
      <div className="absolute inset-0">
        <motion.div 
          className="w-full h-full absolute inset-0"
          key={`${isMobile ? 'mobile' : 'desktop'}-hero`}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ 
            duration: 0.8, 
            ease: "easeInOut"
          }}
        >
          <div 
            className="w-full h-full hero-image"
            style={{
              backgroundImage: `url(${currentImage})`,
              backgroundSize: 'cover',
              backgroundPosition: isMobile ? 'center center' : 'center',
              backgroundRepeat: 'no-repeat'
            }}
          />
        </motion.div>
        
        {/* Smart mobile overlay - lighter and more strategic */}
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-black/20 to-transparent sm:bg-black/40 z-10"></div>
        
        {/* Additional text-area specific overlay for mobile */}
        <div className="absolute inset-0 bg-gradient-radial-mobile sm:hidden z-10"></div>
      </div>

      {/* Content */}
      <div className="relative z-20 h-full flex items-center">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="max-w-5xl mx-auto text-center">
            <motion.div 
              className="glass-effect-mobile sm:glass-effect rounded-xl sm:rounded-2xl p-6 sm:p-8 md:p-12"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, ease: "easeOut" }}
            >
              <motion.h1 
                className="text-4xl sm:text-5xl md:text-7xl font-light mb-4 sm:mb-6 mobile-hero-text"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 1, delay: 0.3 }}
              >
                <span className="text-gradient">{title}</span>
              </motion.h1>
              
              <motion.p 
                className="text-lg sm:text-xl md:text-2xl text-secondary/90 mb-6 sm:mb-8 leading-relaxed max-w-4xl mx-auto mobile-hero-subtitle"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 1, delay: 0.5 }}
              >
                {subtitle}
              </motion.p>

              {description && (
                <motion.p 
                  className="text-base sm:text-lg text-secondary/80 mb-8 sm:mb-12 max-w-3xl mx-auto leading-relaxed mobile-hero-subtitle"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 1, delay: 0.6 }}
                >
                  {description}
                </motion.p>
              )}

              <motion.div 
                className="flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-6"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 1, delay: 0.8 }}
              >
                {primaryCTA && (
                  <>
                    {primaryCTA.href ? (
                      <Link 
                        to={primaryCTA.href} 
                        className="btn-primary text-base sm:text-lg"
                      >
                        {primaryCTA.text}
                      </Link>
                    ) : (
                      <button 
                        onClick={primaryCTA.onClick}
                        className="btn-primary text-base sm:text-lg"
                      >
                        {primaryCTA.text}
                      </button>
                    )}
                  </>
                )}
                {secondaryCTA && (
                  <>
                    {secondaryCTA.href ? (
                      <Link 
                        to={secondaryCTA.href} 
                        className="btn-secondary text-base sm:text-lg"
                      >
                        {secondaryCTA.text}
                      </Link>
                    ) : (
                      <button 
                        onClick={secondaryCTA.onClick}
                        className="btn-secondary text-base sm:text-lg"
                      >
                        {secondaryCTA.text}
                      </button>
                    )}
                  </>
                )}
              </motion.div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero; 